import { lsConstants } from "@/constants";
import { unixSeconds } from "@/service/date.ts";
import { asStage, stageConfig, stageUrls } from "@supply25/shared/stage";
import { almostExpired, AuthError, authUrl, ExpiredError } from "./client";
import { isFeatureFlagOn } from "@/service/featureFlags";

const STAGE = asStage(import.meta.env.VITE_STAGE);
const urls = stageUrls(STAGE);

export const APP_URL = urls.app;
// In prod the SSR app owns the real domain — there is no poc hostname, so the
// flag only switches link shape there, not host.
const newAssessmentAppUrl = stageConfig(STAGE).prod
  ? urls.assessmentApp
  : urls.newAssessmentApp;
export const ASSESSMENT_APP_URL = isFeatureFlagOn("newAssApp")
  ? newAssessmentAppUrl
  : urls.assessmentApp;
export const NEW_ASSESSMENT_APP_URL = newAssessmentAppUrl;
export const USER_SERVICE_URL = urls.user;
export const TENDER_PACKAGE_SERVICE_URL = urls.tenderPackage;
export const ASSESSMENT_SERVICE_URL = urls.assessment;

export const fetchWrapper = async (
  fetchUrl: string,
  fetchOptions: RequestInit
) => {
  // 1a.  Get expiration date from local storage
  const expDate = localStorage.getItem(lsConstants.TOKEN_EXPIRES);

  // 1b. User hasn't logged in yet so redirect to login
  if (!expDate) {
    window.location.href = authUrl(
      "/login",
      window.location.pathname + window.location.search
    );
    throw new AuthError("Please log in");
  }

  // 1c.  Is cookie almost expired?
  if (almostExpired(expDate)) {
    // 1d.  If yes, then refetch token first then make the api call
    const response = await fetch(`${USER_SERVICE_URL}/session`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
    });

    if (!response.ok) {
      // Token isn't valid anymore
      // This will render the RootBoundary and ask user to login
      throw new ExpiredError("Session expired");
    }

    const { expiresIn } = await response.json();
    if (expiresIn) {
      const expirationDate = unixSeconds() + expiresIn;
      localStorage.setItem(lsConstants.TOKEN_EXPIRES, expirationDate);
    }
  }

  const response = await fetch(fetchUrl, fetchOptions);

  if (!response.ok) {
    const responseBody = await response.json();
    const errorMessage =
      typeof responseBody?.error === "string"
        ? responseBody.error
        : `Error calling api: ${fetchUrl}`;
    if (response.status === 401) {
      // Token isn't valid anymore
      // This will render the RootBoundary and ask user to login
      throw new ExpiredError("Session expired");
    }
    throw new Error(errorMessage);
  }

  return await response.json();
};
